import { FC, memo } from "react";
import { styled } from "@mui/material";
import { noteDisciplines, noteStats } from "../../utils/landing-data";
import { bg, bp, fluid, font, w } from "../../styles/landing-tokens";
import { Container, DisciplineRow, DisplayHeading, Eyebrow } from "./shared";
import { useReveal } from "./reveal";

const Section = styled("section")({
  position: "relative",
  background: bg,
  /** Figma: hero ends at y=1324, the eyebrow sits at y=1558. */
  paddingTop: 234,
  /** The stats card is the last thing in the section — Figma ends flush at y=2624. */
  paddingBottom: 0,

  [bp.md]: { paddingTop: 120, paddingBottom: 120 },
});

/** Left column 569px, right column 854px, 690px apart — Figma 188 / 878. */
const Grid = styled("div")({
  display: "grid",
  gridTemplateColumns: "569px 854px",
  columnGap: 121,
  alignItems: "start",

  /** Figma: the right column starts at y=1603, the heading 30px lower. */
  "& > :first-of-type": { marginTop: 30 },

  [bp.lg]: {
    gridTemplateColumns: "1fr 1.4fr",
    columnGap: 60,
  },
  [bp.md]: {
    gridTemplateColumns: "1fr",
    rowGap: 60,
  },
});


const Disciplines = styled("div")({
  marginTop: 40,
  borderTop: `1.25px solid ${w(0.05)}`,
});

const RightColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 60.2,
});

const Paragraphs = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 40.1,
});

/**
 * Body copy: DM Sans 400, 23.8/44.1, justified, white 66%.
 * The first paragraph opens with a 60px drop cap ("T").
 */
const Paragraph = styled("p")({
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: fluid(17, 23.8),
  lineHeight: 1.85,
  textAlign: "justify",
  color: w(0.66),
  margin: 0,

  "& strong": {
    fontWeight: 500,
    color: w(0.95),
  },

  /**
   * Figma draws the opening "T" as a separate 60px layer sitting on the first
   * line — the copy does not wrap around it, so this is an oversized initial
   * with zero line-height (it overflows upward) rather than a floated drop cap.
   */
  "&.lead::first-letter": {
    fontSize: 60,
    lineHeight: 0,
    fontWeight: 400,
    color: "#FFFFFF",
    /**
     * The 60px cap's advance leaves a wide gap before "he"; pull the following
     * text back under the T's right arm. Set in em so it scales with the cap
     * (incl. the 44px mobile size below).
     */
    marginRight: "-0.13em",
  },

  [bp.sm]: {
    "&.lead::first-letter": { fontSize: 44 },
  },
});

/** Signature block: name + role on the left, location on the right. */
const Signature = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 24,
  paddingTop: 41,
  borderTop: `1.25px solid ${w(0.06)}`,

  [bp.sm]: { flexDirection: "column", alignItems: "flex-start", gap: 12 },
});

const SignatureName = styled("div")({
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 25.1,
  lineHeight: "37.6px",
  letterSpacing: "-0.025em",
  fontStyle: "italic",
  color: w(0.85),
});

const SignatureRole = styled("div")({
  marginTop: 5,
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 13.8,
  lineHeight: "20.7px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: w(0.35),
});

/** Four stat cells inside a rounded 6%-white card, separated by hairlines. */
const Stats = styled("div")({
  display: "grid",
  /** Figma cell widths: 212 / 212 / 212 / 252. */
  gridTemplateColumns: "212fr 212fr 212fr 252fr",
  gap: 1,
  background: w(0.06),
  border: `0.7px solid ${w(0.06)}`,
  borderRadius: 20.1,
  overflow: "hidden",

  [bp.sm]: { gridTemplateColumns: "repeat(2, 1fr)" },
});

/** Figma cell: 142px tall — 30.1 padding, 53 value, 10 gap, 19 label, 30.1 padding. */
const Stat = styled("div")({
  background: bg,
  padding: "30.1px",
});

const StatValue = styled("div")({
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: fluid(26, 35.1),
  lineHeight: "52.6px",
  letterSpacing: "-0.036em",
  color: "#FFFFFF",
});

const StatLabel = styled("div")({
  marginTop: 10,
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 12.5,
  lineHeight: "18.8px",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: w(0.4),
  /** Figma keeps "publications & books" on one line. */
  whiteSpace: "nowrap",

  [bp.sm]: { whiteSpace: "normal" },
});

const EyebrowRow = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: 26,
});

export const PersonalNote: FC = memo(() => {
  const heading = useReveal();
  const disciplines = useReveal({ delay: 120 });
  const paragraphs = useReveal();
  const signature = useReveal({ delay: 100 });
  const stats = useReveal({ variant: "scale", delay: 120 });

  return (
    <Section id="about">
    <Container>
      <EyebrowRow>
        <Eyebrow>A personal note</Eyebrow>
      </EyebrowRow>

      <Grid>
        <div>
          <DisplayHeading ref={heading.ref} style={heading.style}>
            {"Interdisciplinary\nby practice,\nnot by accident."}
          </DisplayHeading>

          <Disciplines ref={disciplines.ref} style={disciplines.style}>
            {noteDisciplines.map((d) => (
              <DisciplineRow key={d.no} divided>
                <span className="no">{d.no}</span>
                <span className="name">{d.name}</span>
                <span className="meta">{d.meta}</span>
              </DisciplineRow>
            ))}
          </Disciplines>
        </div>

        <RightColumn>
          <Paragraphs ref={paragraphs.ref} style={paragraphs.style}>
            <Paragraph className="lead">
              <strong>The intersection of disciplines</strong> is a challenging place to
              innovate, requiring
              expertise in not just one but multiple fields. Yet the rewards of working at
              such intersections, I’ve found to be well worthwhile. This multi-disciplinary
              thread is the hallmark of my professional career, combining fields as diverse
              as AI, derivatives trading, Web3, and biotechnology.
            </Paragraph>

            <Paragraph>
              <strong>My latest venture, Gamma Prime,</strong> reaches across the fields of
              derivatives, Web3, and AI to tokenize the yield of financial products. This
              will open the flood gates to authentic yield, previously only available to
              ultra-wealthy hedge fund clients and gigantic institutional endowments.
            </Paragraph>

            <Paragraph>
              <strong>Accomplishing this has required</strong> an unprecedented
              multi-disciplinary team of veteran Web3 innovators, expert machine learning
              developers, professional Chicago derivatives traders, and elite providers of
              hedge fund infrastructure. Gamma Prime is truly a tribute to the power of
              interdisciplinary innovation.
            </Paragraph>
          </Paragraphs>

          <Signature ref={signature.ref} style={signature.style}>
            <div>
              <SignatureName>— Evan Szu, PhD</SignatureName>
              <SignatureRole>Founder &amp; CEO, Gamma Prime</SignatureRole>
            </div>
            <Eyebrow>Palo Alto, California</Eyebrow>
          </Signature>

          <Stats ref={stats.ref} style={stats.style}>
            {noteStats.map((s) => (
              <Stat key={s.label}>
                <StatValue>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </Stats>
        </RightColumn>
      </Grid>
    </Container>
    </Section>
  );
});
